import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      nav("/login", { state: { resetComplete: true } });
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail) || requestError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-xl items-center px-6 py-16">
      <form onSubmit={submit} className="w-full rounded-2xl border border-neutral-200 bg-white p-7" data-testid="reset-password-form">
        <p className="eyebrow mb-2">Account recovery</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">Choose a new password</h1>
        <p className="mt-3 text-sm text-neutral-500">Use at least 6 characters.</p>
        {!token && <p className="mt-5 text-sm text-red-600" data-testid="reset-password-token-error">This reset link is incomplete.</p>}
        {error && <p className="mt-5 text-sm text-red-600" data-testid="reset-password-error">{error}</p>}
        <div className="mt-6">
          <Label htmlFor="reset-password">New password</Label>
          <div className="relative mt-1">
            <Input
              id="reset-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={6}
              required
              className="pr-11"
              disabled={!token}
              data-testid="reset-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-neutral-500"
              aria-label={showPassword ? "Hide password" : "Show password"}
              data-testid="reset-password-toggle"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          disabled={!token || loading}
          className="mt-6 w-full rounded-full bg-[hsl(var(--primary))]"
          data-testid="reset-password-submit"
        >
          {loading ? "Saving…" : "Save new password"}
        </Button>
        <Link to="/login" className="mt-6 inline-block text-sm font-medium text-[hsl(var(--primary))]" data-testid="reset-password-back-login">
          Back to sign in
        </Link>
      </form>
    </main>
  );
}